import React, { useState, Fragment, useEffect } from "react";
import parse from 'html-react-parser';
import { Box, useMediaQuery, useTheme, Typography, Button, Switch } from '@mui/material';
import { CurrentLesson } from "../../../interfaces/currentLesson";
import { Promo } from "../../../interfaces/promo";
import { Accelerator } from "../../../interfaces/accelerator";
import { clickProduct, viewProduct, videoAutoplayToggleEvent } from "../../mixPanelEvents/mixPanelEvents";
import styles from '../nextLesson/nextLesson.module.css'; // Temporarily using existing styles
import { toggleAutoplay } from "../../../helpers/utils";

interface NextLessonModalProps {
    playerTitleHeight: number;
    playerHeight: number;
    currentLesson: CurrentLesson;
    promo?: Promo | null;
    callback?: (value: boolean) => void;
    modalTitle: string;
    modalAction: () => void;
    modalBtnText: string;
    accelerator: Accelerator;
    handleReplay: () => void;
    autoplayNextForCourse: boolean;
    setAutoplayNextForCourse: (value: boolean) => void;
    source?: string
}

const COUNTDOWN_NUMEBR = 7;

const NextLessonModal: React.FC<NextLessonModalProps> = ({
    playerTitleHeight,
    playerHeight,
    currentLesson,
    promo,
    callback,
    modalTitle,
    modalAction,
    modalBtnText,
    accelerator,
    handleReplay,
    autoplayNextForCourse,
    setAutoplayNextForCourse,
    source
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
    const [countdown, setCountdown] = useState<number>(COUNTDOWN_NUMEBR);

    useEffect(() => {
        if (promo !== undefined && promo !== null) {
            viewProduct("Video", "Banner", "", "End of Video", promo.name);
        }
    }, [promo]);

    // Countdown timer effect
    useEffect(() => {
        if (countdown > 0 && autoplayNextForCourse) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0) {
            // Auto action when countdown reaches 0
            const timer = setTimeout(() => {
                if(autoplayNextForCourse) {
                    modalAction();
                }
            }, 900);          
            return () => clearTimeout(timer);  
        }
    }, [countdown, modalAction, autoplayNextForCourse]);

    useEffect(() => {
        setCountdown(COUNTDOWN_NUMEBR);
        toggleAutoplay(autoplayNextForCourse, accelerator.ID);
    }, [autoplayNextForCourse]);


    const handleModalAction = (): void => {
        modalAction();
    }

    const handleCloseModal = (): void => {
        if (callback) {
            callback(!isModalOpen);
        }
    };

    const handleNoThanksClick = (): void => {
        handleCloseModal();
    };

    const handlePromoClick = (): void => {
        if (promo) {
            clickProduct("Video", "Banner", "", promo.ID, promo.name, "End of Video");
            window.location.href = promo.url;
        }
    };

    const handleAutoplayToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoplayNextForCourse(event.target.checked);
        let status = event.target.checked ? 'On' : 'Off';
        videoAutoplayToggleEvent(status, currentLesson, accelerator, 'End Screen');
    };

return (
    <Box
        style={{
            position: 'absolute',
            top: source == "sc" ? "-1px" : (playerTitleHeight-1) + "px",
            height: isMobile ? playerHeight : "auto",
            left: 0,
            right: 0,
            bottom: 0
        }}
        sx={{
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all'
        }}
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
    >
        {/* Test sadržaj */}
        <Box
            sx={{
                height: isMobile ? "100%" : "auto",
                display: 'flex',
                padding: isMobile ? '12px 0px 24px 0px' : '36px 0px',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isMobile ? '12px' : '24px',
                background: 'rgba(6, 6, 6, 0.80)',
                width: isMobile ? '100%' : '660px',
                justifyContent: 'center'
            }}
        >
            <Typography
                sx={{
                    color: '#FFF',
                    textAlign: 'center',
                    fontFamily: 'Repro, sans-serif',
                    fontSize: isMobile ? '24px' : '36px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: isMobile ? '100%' : '122%'
                }}
            >
                {modalTitle}
            </Typography>

            {/* Autoplay row */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '12px'
                }}
            >
                <Typography
                    sx={{
                        color: '#FFF',
                        fontFamily: 'Repro, sans-serif',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '122%'
                    }}
                >
                    {window.pg?.l10n.auto_paly}
                </Typography>
                <Switch
                    checked={autoplayNextForCourse}
                    onChange={handleAutoplayToggle}
                    sx={{
                        width: '32px',
                        height: '20px',
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                            padding: '1px',
                            '&.Mui-checked': {
                                color: '#FFFFFF',
                                transform:'translateX(12px)',
                                '& + .MuiSwitch-track': {
                                    backgroundColor: '#FD3300',
                                    opacity: 1,
                                },
                            },
                        },
                        '& .MuiSwitch-thumb': {
                            backgroundColor: '#FFFFFF',
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiSwitch-track': {
                            backgroundColor: '#B7B7BF',
                            opacity: 1,
                            borderRadius: '10px',
                        },
                    }}
                />
            </Box>

            {/* Countdown Timer */}
            {autoplayNextForCourse && <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    width: isMobile ? '48px' : '63px',
                    height: isMobile ? '48px' : '64px',
                    minHeight: isMobile ? '48px' : '64px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '9999px',
                    background: 'rgba(255, 255, 255, 0.25)',
                }}
            >
                {/* Progress Circle SVG */}
                <svg
                    width={isMobile ? "48" : "63"}
                    height={isMobile ? "48" : "64"}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transform: 'rotate(-90deg)'
                    }}
                >
                    <circle
                        cx={isMobile ? "24" : "31.5"}
                        cy={isMobile ? "24" : "32"}
                        r={isMobile ? "22" : "29.5"}
                        stroke="#FFFFFF"
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * (isMobile ? 22 : 29.5)}`}
                        strokeDashoffset={`${2 * Math.PI * (isMobile ? 22 : 29.5) * (countdown / COUNTDOWN_NUMEBR)}`}
                        style={{
                            transition: 'stroke-dashoffset 1s ease-in-out'
                        }}
                    />
                </svg>

                {/* Countdown Number */}
                <Typography
                    sx={{
                        color: '#FFFFFF',
                        fontFamily: 'Repro, sans-serif',
                        fontSize: isMobile ? '18px' : '24px',
                        fontWeight: 700,
                        lineHeight: '1',
                        zIndex: 1
                    }}
                >
                    {window.pg?.l10n?.countdown?.[countdown] || countdown}
                </Typography>
            </Box>}
            {!autoplayNextForCourse && <Box
                onClick={handleModalAction}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    width: isMobile ? '48px' : '63px',
                    height: isMobile ? '48px' : '64px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '9999px',
                    background: 'rgba(255, 255, 255, 0.25)',
                }}>
                <svg 
                    width={isMobile ? "48" : "63"} 
                    height={isMobile ? "48" : "64"} 
                    viewBox="0 0 64 64" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.5 31.5C0.5 14.103 14.603 0 32 0C49.397 0 63.5 14.103 63.5 31.5V32.5C63.5 49.897 49.397 64 32 64C14.603 64 0.5 49.897 0.5 32.5V31.5Z" fill="white" fill-opacity="0.25"/>
                    <mask 
                        id="mask0_53_21396" 
                        style={{ maskType: 'alpha' }} 
                        maskUnits="userSpaceOnUse" 
                        x="14" 
                        y="14" 
                        width="36" 
                        height="36">
                    <rect 
                        x="14" 
                        y="14" 
                        width="36" 
                        height="36" 
                        fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#mask0_53_21396)">
                    <path d="M23.0752 42.5L30.5752 32L23.0752 21.5H26.7502L34.2502 32L26.7502 42.5H23.0752ZM32.0002 42.5L39.5002 32L32.0002 21.5H35.6752L43.1752 32L35.6752 42.5H32.0002Z" fill="white"/>
                    </g>
                </svg>
            </Box>}

            {/* Buttons Row */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? '8px' : '16px',
                    width: isMobile ? '100%' : 'auto',
                    padding: isMobile ? '0px 25px' : '0px'
                }}
            >
                {/* Transparent Button */}
                <Button
                    onClick={handleReplay}
                    sx={{
                        display: 'flex',
                        width: isMobile ? '150px' : '195px',
                        padding: '9px 16px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1px',
                        alignSelf: isMobile ? 'stretch' : 'auto',
                        borderRadius: '30px',
                        border: '2px solid #FFF',
                        background: 'rgba(92, 189, 129, 0.00)',
                        color: '#FFF',
                        textAlign: 'center',
                        fontFamily: 'Repro, sans-serif',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '122%',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '2px solid #FFF'
                        }
                    }}
                >
                    {window.pg?.l10n.repay_lesson}
                </Button>

                {/* Main Action Button */}
                <Button
                    onClick={handleModalAction}
                    sx={{
                        display: 'flex',
                        width: isMobile ? '150px' : '266px',
                        padding: isMobile ? '9px 15px' : '9px 41px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1px',
                        borderRadius: '30px',
                        border: '0',
                        background: '#FD3300',
                        color: '#FFF',
                        textAlign: 'center',
                        fontFamily: 'Repro, sans-serif',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '122%',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                            background: '#DB2C00'
                        }
                    }}
                >
                    <Typography sx={{
                        fontWeight: 400, 
                        whiteSpace: "nowrap",
                        fontSize: '16px',
                        fontFamily: 'Repro, sans-serif',
                        }}>{window.pg?.l10n.up_next}</Typography>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginLeft: '2px',
                            fontSize: '16px',
                            fontFamily: 'Repro, sans-serif',
                        }}
                    >{parse(modalBtnText)}</Typography>
                </Button>
            </Box>
        </Box>
    </Box>
);
};

export default NextLessonModal;